import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import { ISubTopic } from '../SubTopicSlice';
import { IBaseUser } from '../UserSlice';
import {
  adminGetQuestionList,
  adminLoadMoreQuestionList,
  adminCreateNewQuestion,
  adminCreateNewQuestions,
  adminUpdateQuestion,
  adminApproveQuestion,
  adminDeleteQuestion,
  adminGetQuestionById,
  adminGetQuestions,
  adminLoadMoreQuestions,
} from './QuestionAdminApi';
import {
  getAIMarking,
  getAssessQuestions,
  getQuestionsBySubTopic,
  getQuestionsByCurriculumSubject,
  getRandomQuestionsBySubTopic,
} from './QuestionApi';

export type TQuestionDifficulty = 'EASY' | 'MEDIUM' | 'HARD' | 'ANY';

export interface IQuestionTag {
  _id: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
}

export interface IQuestionDetailAnswer {
  _id?: string;
  order: number;
  isCorrect?: boolean;
  content: string;
  openaiResponse?: string;
  openaiExplanation?: string;
}

export interface IQuestionSubTopic {
  subTopic: ISubTopic;
  paper: string;
}

export const QUESTION_TYPES = {
  STRUCTURED: 'STRUCTURED',
  UNSTRUCTURED: 'UNSTRUCTURED',
};

export type TQuestionType = typeof QUESTION_TYPES[keyof typeof QUESTION_TYPES];

export interface IQuestionSubQuestion {
  _id?: string;
  question: string;
  marks: number;
  markingScheme: string;
  sortingOrder: number;
}

export interface IQuestion {
  _id: string;
  type: TQuestionType;
  subTopics: IQuestionSubTopic[];
  difficulty: TQuestionDifficulty;
  source?: string;
  marks: number;
  tags: Array<string>;
  details: string;
  markingSchema: string;
  answers: Array<IQuestionDetailAnswer>;
  isApproved: boolean;
  approvedBy?: IBaseUser;
  createdAt: string;
  createdBy: IBaseUser;
  deletedAt?: string;
  updatedAt?: string;
  updatedBy?: IBaseUser;
  openEnded?: { question: string };
  theoryQuestion?: { question: string };
  openAimarking?: string; // for practise questions
  aiHint?: string;
  isOpenEnded?: boolean;
  // Structured Essay Question
  subQuestions?: Array<IQuestionSubQuestion>;
}

export interface IAssessQuestionAnswer {
  _id: string;
  details: string;
  isCorrect: boolean;
}
export interface IAssessQuestion {
  _id: string;
  question: string;
  answers: Array<IAssessQuestionAnswer>;
}

export interface IQuestionState {
  status: 'idle' | 'loading' | 'rejected';
  list: Array<IQuestion>;
  question?: IQuestion;
  count: number;
  assessQuestions: Array<IAssessQuestion>;
  questionsData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IQuestion>;
  };
}

const initialState: IQuestionState = {
  status: 'idle',
  list: [],
  count: 0,
  question: undefined,
  assessQuestions: [],
  questionsData: undefined,
};

export const QuestionSlice = createSlice({
  name: 'question',
  initialState,
  reducers: {
    resetQuestion: (state) => {
      state.question = undefined;
    },
    resetQuestionList: (state) => {
      state.list = [];
    },
    resetAssessQuestions: (state) => {
      state.assessQuestions = [];
    },
    resetAIMarking: (state) => {
      const newList = [...state.list];
      state.list = newList.map((item) => {
        delete item.openAimarking;
        return item;
      });
    },
    resetQuestionsData: (state) => {
      state.questionsData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // ADMIN QUESTION LIST
      .addCase(adminGetQuestionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetQuestionList.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload.docs;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminGetQuestionList.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN LOAD MORE
      .addCase(adminLoadMoreQuestionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminLoadMoreQuestionList.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = [...state.list, ...action.payload.docs];
        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminLoadMoreQuestionList.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin Load More Questions
      .addCase(adminLoadMoreQuestions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminLoadMoreQuestions.fulfilled, (state, action) => {
        state.status = 'idle';

        const newList = [...state.list, ...action.payload.docs];

        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminLoadMoreQuestions.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN GET QUESTIONS BY ID
      .addCase(adminGetQuestionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetQuestionById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.question = action.payload;
      })
      .addCase(adminGetQuestionById.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN CREATE NEW QUESTION
      .addCase(adminCreateNewQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminCreateNewQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count += 1;
        const newList = [...state.list, action.payload];
        state.list = newList;
      })
      .addCase(adminCreateNewQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN CREATE NEW QUESTIONS (BULK)
      .addCase(adminCreateNewQuestions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminCreateNewQuestions.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count += action.payload.length;
        const newList = [...state.list, ...action.payload];
        state.list = newList;
      })
      .addCase(adminCreateNewQuestions.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN UPDATE QUESTION
      .addCase(adminUpdateQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminUpdateQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.list;

        const index = newList.findIndex(
          (item) => item._id === action.payload._id
        );

        if (index > -1) {
          newList[index] = action.payload;
        }

        state.list = newList;

        if (
          state.question &&
          action.payload &&
          state.question._id === action.payload._id
        ) {
          state.question = {
            ...state.question,
            ...action.payload,
          };
        }
      })
      .addCase(adminUpdateQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN APPROVE QUESTION
      .addCase(adminApproveQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminApproveQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.list;
        const index = newList.findIndex(
          (item) => item._id === action.payload._id
        );
        if (index > -1) {
          newList[index] = {
            ...newList[index],
            isApproved: action.payload.isApproved,
            approvedBy: action.payload.approvedBy,
          };
        }
        state.list = newList;
      })
      .addCase(adminApproveQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // ADMIN DELETE QUESTION
      .addCase(adminDeleteQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminDeleteQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.list.filter(
          (question) => question._id !== action.payload._id
        );
        state.list = newList;
        state.count -= 1;
      })
      .addCase(adminDeleteQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin Get Questions
      .addCase(adminGetQuestions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetQuestions.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload.docs;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminGetQuestions.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Questions by SubTopic
      .addCase(getQuestionsBySubTopic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getQuestionsBySubTopic.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload;
      })
      .addCase(getQuestionsBySubTopic.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Random Questions by SubTopic
      .addCase(getRandomQuestionsBySubTopic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getRandomQuestionsBySubTopic.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload;
      })
      .addCase(getRandomQuestionsBySubTopic.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Tutor Assess Questions
      .addCase(getAssessQuestions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAssessQuestions.fulfilled, (state, action) => {
        state.status = 'idle';
        if (action.payload.length) {
          state.assessQuestions = action.payload;
        }
      })
      .addCase(getAssessQuestions.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get AI Marking
      .addCase(getAIMarking.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAIMarking.fulfilled, (state, action) => {
        state.status = 'idle';
        if (action.payload.questionId) {
          const newList = [...state.list];
          const index = newList.findIndex(
            (item) => item._id === action.payload.questionId
          );
          if (index > -1) {
            newList[index] = {
              ...newList[index],
              openAimarking: action.payload.marking,
            };
          }
          state.list = newList;
        }
      })
      .addCase(getAIMarking.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Questions by Curriculum Subject
      .addCase(getQuestionsByCurriculumSubject.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getQuestionsByCurriculumSubject.fulfilled, (state, action) => {
        state.status = 'idle';
        const result = action.payload;

        if (result.offset === 0) {
          // Initial load - replace the list
          state.questionsData = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };
        } else {
          // Load more - append to existing list
          state.questionsData = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: [
              ...(state.questionsData?.list || []),
              ...(result.docs || []),
            ],
          };
        }
      })
      .addCase(getQuestionsByCurriculumSubject.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const {
  resetQuestion,
  resetQuestionList,
  resetAssessQuestions,
  resetAIMarking,
  resetQuestionsData,
} = QuestionSlice.actions;

export const getQuestionState = (state: RootState): RootState['question'] =>
  state.question;

export * from './QuestionAdminApi';
export * from './QuestionApi';
export default QuestionSlice.reducer;
